/**
 * Hand a fetched binary response to the browser.
 *
 * Routes that return files are auth-guarded, so a bare `window.open` on the URL
 * would arrive without a bearer token. Callers fetch through the authed API
 * client and pass the `Response` here instead.
 *
 * Object-URL lifetime differs by mode, which is the whole reason these are two
 * functions: a download is finished the moment the synthetic click dispatches,
 * so its URL is revoked immediately, while a new tab keeps reading from its URL
 * for as long as it is open and the caller has to hold that one.
 */

/** Save the response as `filename`. Revokes its object URL immediately. */
export async function downloadBlobResponse(
  res: Response,
  filename: string,
): Promise<void> {
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  // The click has been dispatched; the URL is no longer needed.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Open the response in a new tab. Returns the object URL, which the caller must
 * keep alive while the tab may still be reading it and revoke on unmount.
 */
export async function openBlobResponse(res: Response): Promise<string> {
  const url = URL.createObjectURL(await res.blob());
  window.open(url, "_blank", "noopener");
  return url;
}
